/**
 * WGS84 ↔ local frame conversion, and the floating origin (non-negotiable #4).
 *
 * ## Three frames, and why
 *
 * 1. **Geodetic** — latitude, longitude, altitude above the ellipsoid. What the data sources
 *    speak: tiles, airports, the sun position. Doubles throughout.
 *
 * 2. **Local sim frame** — metres east / north / up from a moving origin, which is where the
 *    flight model, the gear and the terrain all live. Its defining property is that **`up` is
 *    true geodetic altitude**, so `altitudeM === -positionNed.z` stays exactly true everywhere
 *    on Earth and the atmosphere, the altimeter and the terrain heights all agree without
 *    anybody converting anything. It is therefore *not* a tangent plane: it is a tangent plane
 *    with the Earth's curvature folded back into the vertical axis.
 *
 * 3. **Render frame** — the tangent plane at the origin, which is what you actually have to
 *    draw. Going from (2) to (3) is a single subtraction, the **curvature drop**
 *    `d²/2R` — and that one term is what makes the horizon sit below eye level and drop
 *    further as you climb, terrain 60 km away disappear over the edge of the world, and a
 *    coastline bend away instead of running to infinity. Without it a "realistic" sim looks
 *    like a flat table no matter how good the terrain is.
 *
 * The split matters because the two consumers want different things: physics wants altitude to
 * be a coordinate, and the GPU wants a plane. Doing it any other way means either the altimeter
 * lies by 8 m at the edge of the origin's range, or the horizon is flat.
 *
 * ## Precision policy
 *
 * Everything here is double precision — JS numbers are doubles and three's `Vector3` stores
 * plain numbers, so this is free. FP32 exists only past the renderer boundary. The floating
 * origin keeps render-space coordinates under ~10 km, where an FP32 float still resolves to
 * well under a millimetre and the cockpit does not shake (see {@link FloatingOrigin}).
 *
 * `updateFixed` / `updateFrame`: neither. {@link FloatingOrigin.update} is called once per
 * physics tick from the world system; everything else is a pure function. Allocation-free —
 * conversions write into caller-supplied output structs.
 */

import { Matrix4, Quaternion, Vector3 } from 'three';
import {
  EARTH_MEAN_RADIUS_M,
  ORIGIN_REBASE_DISTANCE_M,
  WGS84_ECCENTRICITY_SQ,
  WGS84_SEMI_MAJOR_AXIS_M,
  WGS84_SEMI_MINOR_AXIS_M,
} from './geo.js';
import { DEG2RAD, RAD2DEG } from '../core/math.js';

/** Module-level scratch for {@link LocalFrame.transformFrom}; see the scratch contract in math.ts. */
const transformMatrix = /* #__PURE__ */ new Matrix4();

/**
 * Change of basis from tangent ENU (x east, y north, z up) to render axes
 * (x east, y up, z south) — a −90° rotation about the east axis.
 *
 * The counterpart of `AERO_TO_RENDER_QUAT` in `sim/frames.ts`, which does the same job for the
 * flight model's NED. Two conventions, two conversions, both applied at exactly one boundary.
 */
export const ENU_TO_RENDER_QUAT = /* #__PURE__ */ new Quaternion(-Math.SQRT1_2, 0, 0, Math.SQRT1_2);
export const RENDER_TO_ENU_QUAT = /* #__PURE__ */ new Quaternion(Math.SQRT1_2, 0, 0, Math.SQRT1_2);

/** A point on the planet. */
export interface Geodetic {
  latitudeDeg: number;
  longitudeDeg: number;
  /** Altitude above the WGS84 ellipsoid [m]. */
  altitudeM: number;
}

export function makeGeodetic(latitudeDeg = 0, longitudeDeg = 0, altitudeM = 0): Geodetic {
  return { latitudeDeg, longitudeDeg, altitudeM };
}

/** Earth-centred, Earth-fixed cartesian position [m]. */
export interface Ecef {
  x: number;
  y: number;
  z: number;
}

export function makeEcef(): Ecef {
  return { x: 0, y: 0, z: 0 };
}

// --- geodetic ↔ ECEF -------------------------------------------------------------------------

/** Radius of curvature in the prime vertical at this latitude [m]. */
function primeVerticalRadiusM(sinLat: number): number {
  return WGS84_SEMI_MAJOR_AXIS_M / Math.sqrt(1 - WGS84_ECCENTRICITY_SQ * sinLat * sinLat);
}

/** Geodetic → ECEF. Exact. */
export function geodeticToEcef(
  latitudeDeg: number,
  longitudeDeg: number,
  altitudeM: number,
  out: Ecef,
): Ecef {
  const latRad = latitudeDeg * DEG2RAD;
  const lonRad = longitudeDeg * DEG2RAD;
  const sinLat = Math.sin(latRad);
  const cosLat = Math.cos(latRad);
  const sinLon = Math.sin(lonRad);
  const cosLon = Math.cos(lonRad);

  const n = primeVerticalRadiusM(sinLat);
  const xy = (n + altitudeM) * cosLat;

  out.x = xy * cosLon;
  out.y = xy * sinLon;
  out.z = (n * (1 - WGS84_ECCENTRICITY_SQ) + altitudeM) * sinLat;
  return out;
}

/** Second eccentricity squared, for Bowring's formula. */
const SECOND_ECCENTRICITY_SQ =
  (WGS84_SEMI_MAJOR_AXIS_M * WGS84_SEMI_MAJOR_AXIS_M -
    WGS84_SEMI_MINOR_AXIS_M * WGS84_SEMI_MINOR_AXIS_M) /
  (WGS84_SEMI_MINOR_AXIS_M * WGS84_SEMI_MINOR_AXIS_M);

/**
 * ECEF → geodetic, by Bowring's closed-form solution.
 *
 * Closed form rather than the usual fixed-point iteration on latitude: it is accurate to well
 * under a millimetre anywhere from the sea floor to orbit, it has no convergence behaviour to
 * reason about, and it costs a bounded handful of transcendentals — which matters because this
 * runs on the physics tick every time the world asks where the aircraft is.
 */
export function ecefToGeodetic(x: number, y: number, z: number, out: Geodetic): Geodetic {
  const p = Math.hypot(x, y);

  if (p < 1e-9) {
    // On the spin axis: longitude is undefined, so call it zero rather than let atan2 pick.
    out.latitudeDeg = z >= 0 ? 90 : -90;
    out.longitudeDeg = 0;
    out.altitudeM = Math.abs(z) - WGS84_SEMI_MINOR_AXIS_M;
    return out;
  }

  const theta = Math.atan2(z * WGS84_SEMI_MAJOR_AXIS_M, p * WGS84_SEMI_MINOR_AXIS_M);
  const sinTheta = Math.sin(theta);
  const cosTheta = Math.cos(theta);

  const latRad = Math.atan2(
    z + SECOND_ECCENTRICITY_SQ * WGS84_SEMI_MINOR_AXIS_M * sinTheta * sinTheta * sinTheta,
    p - WGS84_ECCENTRICITY_SQ * WGS84_SEMI_MAJOR_AXIS_M * cosTheta * cosTheta * cosTheta,
  );
  const sinLat = Math.sin(latRad);
  const n = primeVerticalRadiusM(sinLat);

  out.latitudeDeg = latRad * RAD2DEG;
  out.longitudeDeg = Math.atan2(y, x) * RAD2DEG;
  // cos(lat) → 0 at the poles, so switch to the polar form there rather than divide by it.
  out.altitudeM =
    Math.abs(latRad) < 1.53 // ~87.7°
      ? p / Math.cos(latRad) - n
      : z / sinLat - n * (1 - WGS84_ECCENTRICITY_SQ);
  return out;
}

// --- curvature -------------------------------------------------------------------------------

/**
 * How far the Earth's surface falls away from the tangent plane over a horizontal distance [m].
 *
 * `drop = √(R² + d²) − R`, the exact spherical result, rather than the `d²/2R` small-angle
 * form: they differ by 30 cm at 100 km, which is nothing, but the exact form costs one square
 * root and cannot surprise anyone at the 200 km draw distances the Ultra tier allows.
 *
 * Reference numbers, at the mean Earth radius:
 *   1 km → 0.08 m · 10 km → 7.8 m · 50 km → 196 m · 100 km → 785 m · 200 km → 3.1 km
 *
 * The 10 km figure is why the floating-origin threshold and the curvature term are the same
 * conversation: at the moment the origin rebases, the two frames differ by about the height of
 * a two-storey building.
 */
export function curvatureDropM(
  horizontalDistanceM: number,
  earthRadiusM = EARTH_MEAN_RADIUS_M,
): number {
  return Math.hypot(earthRadiusM, horizontalDistanceM) - earthRadiusM;
}

/**
 * Geometric distance to the horizon from `eyeHeightM` above the surface [m].
 * Exported because it is the right far-plane and terrain-radius hint at altitude, and because
 * it is the number to check the horizon against when the curvature looks wrong.
 */
export function horizonDistanceM(eyeHeightM: number, earthRadiusM = EARTH_MEAN_RADIUS_M): number {
  const h = Math.max(0, eyeHeightM);
  return Math.sqrt(h * (2 * earthRadiusM + h));
}

// --- the local frame -------------------------------------------------------------------------

/**
 * A local east/north/up frame anchored at a geodetic point.
 *
 * Two coordinate flavours come out of it, and the difference between them is the whole point of
 * this file:
 *
 *   - {@link geodeticToLocal} gives the **sim frame**, where `up` is true altitude.
 *   - {@link geodeticToTangent} gives the **render frame**, the plain tangent plane.
 *
 * `localToRender` converts between them by subtracting the curvature drop.
 */
export class LocalFrame {
  /** Where the frame is anchored. */
  readonly origin: Geodetic = makeGeodetic();

  private readonly originEcef: Ecef = makeEcef();
  /** ENU basis vectors in ECEF, rows of the rotation from ECEF to ENU. */
  private eastX = 0;
  private eastY = 0;
  private eastZ = 0;
  private northX = 0;
  private northY = 0;
  private northZ = 0;
  private upX = 0;
  private upY = 0;
  private upZ = 0;
  /** Earth radius at the origin latitude [m] — the right one for the curvature term here. */
  private radiusAtOriginM = EARTH_MEAN_RADIUS_M;

  private readonly scratchEcef: Ecef = makeEcef();
  private readonly scratchGeodetic: Geodetic = makeGeodetic();

  constructor(latitudeDeg = 0, longitudeDeg = 0, altitudeM = 0) {
    this.setOrigin(latitudeDeg, longitudeDeg, altitudeM);
  }

  /** Radius of curvature used for the curvature drop at this origin [m]. */
  get earthRadiusM(): number {
    return this.radiusAtOriginM;
  }

  /** Move the anchor. Everything previously converted is invalidated. */
  setOrigin(latitudeDeg: number, longitudeDeg: number, altitudeM = 0): void {
    this.origin.latitudeDeg = latitudeDeg;
    this.origin.longitudeDeg = longitudeDeg;
    this.origin.altitudeM = altitudeM;

    geodeticToEcef(latitudeDeg, longitudeDeg, altitudeM, this.originEcef);

    const latRad = latitudeDeg * DEG2RAD;
    const lonRad = longitudeDeg * DEG2RAD;
    const sinLat = Math.sin(latRad);
    const cosLat = Math.cos(latRad);
    const sinLon = Math.sin(lonRad);
    const cosLon = Math.cos(lonRad);

    this.eastX = -sinLon;
    this.eastY = cosLon;
    this.eastZ = 0;

    this.northX = -sinLat * cosLon;
    this.northY = -sinLat * sinLon;
    this.northZ = cosLat;

    this.upX = cosLat * cosLon;
    this.upY = cosLat * sinLon;
    this.upZ = sinLat;

    // Gaussian mean radius of curvature at this latitude: the geometric mean of the meridional
    // and prime-vertical radii. Using the mean Earth radius instead would put the horizon out
    // by ~100 m at 100 km near the poles — invisible, but free to get right.
    const n = primeVerticalRadiusM(sinLat);
    const m =
      (WGS84_SEMI_MAJOR_AXIS_M * (1 - WGS84_ECCENTRICITY_SQ)) /
      Math.pow(1 - WGS84_ECCENTRICITY_SQ * sinLat * sinLat, 1.5);
    this.radiusAtOriginM = Math.sqrt(n * m);
  }

  /** ECEF → tangent-plane ENU [m]. */
  ecefToTangent(x: number, y: number, z: number, out: Vector3): Vector3 {
    const dx = x - this.originEcef.x;
    const dy = y - this.originEcef.y;
    const dz = z - this.originEcef.z;
    return out.set(
      this.eastX * dx + this.eastY * dy + this.eastZ * dz,
      this.northX * dx + this.northY * dy + this.northZ * dz,
      this.upX * dx + this.upY * dy + this.upZ * dz,
    );
  }

  /** Tangent-plane ENU [m] → ECEF. */
  tangentToEcef(eastM: number, northM: number, upM: number, out: Ecef): Ecef {
    out.x = this.originEcef.x + this.eastX * eastM + this.northX * northM + this.upX * upM;
    out.y = this.originEcef.y + this.eastY * eastM + this.northY * northM + this.upY * upM;
    out.z = this.originEcef.z + this.eastZ * eastM + this.northZ * northM + this.upZ * upM;
    return out;
  }

  /**
   * Geodetic → tangent-plane ENU [m]. Exact, via ECEF. This is **render space**.
   * Writes `(east, north, up)` into `out.x/.y/.z`.
   */
  geodeticToTangent(
    latitudeDeg: number,
    longitudeDeg: number,
    altitudeM: number,
    out: Vector3,
  ): Vector3 {
    const p = geodeticToEcef(latitudeDeg, longitudeDeg, altitudeM, this.scratchEcef);
    return this.ecefToTangent(p.x, p.y, p.z, out);
  }

  /** Copy another frame's anchor onto this one. */
  copyFrom(other: LocalFrame): void {
    this.setOrigin(other.origin.latitudeDeg, other.origin.longitudeDeg, other.origin.altitudeM);
  }

  /**
   * The rigid motion that takes a point expressed in `source`'s tangent frame into this one.
   *
   * This is what lets a terrain tile be a **rigid body**. A tile is meshed once, in its own
   * tangent frame at its own centre, with the Earth's curvature baked into the vertices; from
   * then on, placing it anywhere — including after a floating-origin rebase — is setting a
   * position and a quaternion. No re-meshing, no per-vertex fixups, and no seams, because the
   * composition of two exact rigid transforms is exact and neighbouring tiles therefore agree
   * along their shared edge to the last bit.
   *
   * The rotation is `B_this · B_sourceᵀ` — both bases are orthonormal ECEF→ENU rotations, so
   * their product is a rotation too — and the translation is where `source`'s origin lands in
   * this frame. Allocation-free apart from what three's `setFromRotationMatrix` does.
   */
  transformFrom(source: LocalFrame, outPosition: Vector3, outRotation: Quaternion): void {
    const s = source;
    // Row-major 3×3, laid into a Matrix4 (which `set` also takes row-major).
    transformMatrix.set(
      this.eastX * s.eastX + this.eastY * s.eastY + this.eastZ * s.eastZ,
      this.eastX * s.northX + this.eastY * s.northY + this.eastZ * s.northZ,
      this.eastX * s.upX + this.eastY * s.upY + this.eastZ * s.upZ,
      0,
      this.northX * s.eastX + this.northY * s.eastY + this.northZ * s.eastZ,
      this.northX * s.northX + this.northY * s.northY + this.northZ * s.northZ,
      this.northX * s.upX + this.northY * s.upY + this.northZ * s.upZ,
      0,
      this.upX * s.eastX + this.upY * s.eastY + this.upZ * s.eastZ,
      this.upX * s.northX + this.upY * s.northY + this.upZ * s.northZ,
      this.upX * s.upX + this.upY * s.upY + this.upZ * s.upZ,
      0,
      0,
      0,
      0,
      1,
    );
    outRotation.setFromRotationMatrix(transformMatrix);
    this.ecefToTangent(s.originEcef.x, s.originEcef.y, s.originEcef.z, outPosition);
  }

  /**
   * {@link transformFrom}, expressed in **render axes** — x east, y up, z south.
   *
   * Almost every caller wants this one. Tile meshes and airport scenery are built in render
   * axes (that is what a `BufferGeometry` feeds the GPU), so placing them with the ENU
   * transform puts north where up belongs and lays the world on its side — which is exactly
   * what it did, spectacularly, the first time this was wired up.
   *
   * Both parts have to be converted, not just the translation: the rotation is conjugated by
   * the same change of basis, `q_render = qC ⊗ q ⊗ qC*`, for the same reason the aerospace
   * conversion in `sim/frames.ts` is a similarity transform.
   */
  transformFromRender(source: LocalFrame, outPosition: Vector3, outRotation: Quaternion): void {
    this.transformFrom(source, outPosition, outRotation);
    outPosition.set(outPosition.x, outPosition.z, -outPosition.y);
    outRotation.premultiply(ENU_TO_RENDER_QUAT).multiply(RENDER_TO_ENU_QUAT);
  }

  /**
   * Geodetic → **sim frame** ENU [m], where `up` is altitude above the origin's datum.
   *
   * This is the conversion the flight model and the terrain both use, and it is the reason an
   * altimeter reading and a terrain height can be compared directly no matter how far from the
   * origin they are.
   */
  geodeticToLocal(
    latitudeDeg: number,
    longitudeDeg: number,
    altitudeM: number,
    out: Vector3,
  ): Vector3 {
    this.geodeticToTangent(latitudeDeg, longitudeDeg, altitudeM, out);
    out.z = altitudeM - this.origin.altitudeM;
    return out;
  }

  /** Tangent-plane ENU → geodetic. Exact inverse of {@link geodeticToTangent}. */
  tangentToGeodetic(eastM: number, northM: number, upM: number, out: Geodetic): Geodetic {
    const p = this.tangentToEcef(eastM, northM, upM, this.scratchEcef);
    return ecefToGeodetic(p.x, p.y, p.z, out);
  }

  /**
   * Sim-frame ENU → geodetic. Exact inverse of {@link geodeticToLocal}.
   *
   * The horizontal part of the sim frame is the tangent-plane ENU *of the point at its own
   * altitude*, and that east/north pair shifts slightly as the point rises — raising a point
   * along its own local vertical tilts it relative to the origin's basis. So the inverse is a
   * one-dimensional root find: for a fixed `(east, north)`, sliding the tangent height `u`
   * traces a line through space, and we want the point on it at the requested altitude.
   *
   * The curvature drop is an excellent first guess (it is the answer to within a centimetre)
   * and the residual maps onto `u` almost exactly one-for-one, so two Newton-free fixed-point
   * steps converge to double precision. Not on any per-frame path: the aircraft's position is
   * converted once a frame for tile selection, and the origin once per rebase.
   */
  localToGeodetic(eastM: number, northM: number, upM: number, out: Geodetic): Geodetic {
    const u = this.exactLocalUpToTangentUp(eastM, northM, upM);
    this.tangentToGeodetic(eastM, northM, u, out);
    out.altitudeM = upM + this.origin.altitudeM; // exact by construction, not by round trip
    return out;
  }

  /** The curvature drop at this horizontal offset from the origin [m]. */
  curvatureDropAt(eastM: number, northM: number): number {
    return curvatureDropM(Math.hypot(eastM, northM), this.radiusAtOriginM);
  }

  /**
   * Sim `up` → render `up`: subtract the curvature drop.
   *
   * Every position crossing the render boundary goes through this, aircraft included — apply it
   * to the terrain only and the aeroplane floats above distant ground and sinks into near ground.
   *
   * Deliberately the cheap spherical form rather than the exact ellipsoidal one that
   * {@link localToGeodetic} solves for: this runs per object and, in the terrain material, per
   * vertex. The two disagree by under a centimetre at 100 km, which is four orders of magnitude
   * below what a pixel can show.
   */
  localUpToTangentUp(eastM: number, northM: number, upM: number): number {
    return upM - this.curvatureDropAt(eastM, northM);
  }

  /** Render `up` → sim `up`. */
  tangentUpToLocalUp(eastM: number, northM: number, tangentUpM: number): number {
    return tangentUpM + this.curvatureDropAt(eastM, northM);
  }

  /**
   * Exact ellipsoidal version of {@link localUpToTangentUp}, for the handful of callers that
   * need the answer to double precision rather than to a centimetre.
   *
   * The spherical drop is the first guess and is already good to ~2 cm at 10 km; the residual
   * altitude error maps onto the tangent height essentially one-for-one, so two fixed-point
   * steps converge. Costs two geodetic inversions — fine once per rebase, far too expensive
   * per vertex, which is why both versions exist.
   */
  exactLocalUpToTangentUp(eastM: number, northM: number, upM: number): number {
    const targetAltitudeM = upM + this.origin.altitudeM;
    let u = this.localUpToTangentUp(eastM, northM, upM);
    for (let i = 0; i < 2; i++) {
      this.tangentToGeodetic(eastM, northM, u, this.scratchGeodetic);
      u += targetAltitudeM - this.scratchGeodetic.altitudeM;
    }
    return u;
  }

  // --- NED convenience -----------------------------------------------------------------------
  //
  // The flight model works in NED (x north, y east, z **down**) because that is what the
  // aerodynamics literature assumes; see src/sim/frames.ts. These wrap the ENU calls so nothing
  // downstream has to remember which of the two it is holding.

  /** Geodetic → sim-frame NED [m]: `(north, east, down)` into `out.x/.y/.z`. */
  geodeticToNed(
    latitudeDeg: number,
    longitudeDeg: number,
    altitudeM: number,
    out: Vector3,
  ): Vector3 {
    this.geodeticToLocal(latitudeDeg, longitudeDeg, altitudeM, out);
    const east = out.x;
    const north = out.y;
    const up = out.z;
    return out.set(north, east, -up);
  }

  /** Sim-frame NED → geodetic. */
  nedToGeodetic(northM: number, eastM: number, downM: number, out: Geodetic): Geodetic {
    return this.localToGeodetic(eastM, northM, -downM, out);
  }
}

// --- floating origin ---------------------------------------------------------------------------

/**
 * What happened when the origin moved.
 *
 * **A rebase is not a pure translation, and this is the one thing to get right when consuming
 * it.** Moving the origin also rotates the east/north basis — meridians converge, and the
 * tangent plane tilts — by roughly `distance / R` radians, about 0.0016 rad for a 10 km hop.
 * That is nothing under the aircraft and it is *not* nothing far away: measured, a point 30 km
 * out lands 0.1 m from where a pure translation predicts, and one 10 km out after a diagonal
 * hop lands 25 m away. Translating a mountain range by the shift would visibly slide it.
 *
 * So there are two kinds of consumer:
 *
 *   - **Anchored to the planet** — terrain tiles, airports, scenery. These hold their own
 *     latitude and longitude and must **re-derive** their local position through
 *     {@link LocalFrame.geodeticToLocal}. That is exact by construction, costs a few
 *     microseconds for a few hundred objects, and is the only correct answer.
 *   - **Anchored to the aircraft** — particles, contrails, tracers, debris. These have no
 *     geodetic identity to re-derive from, so they are carried across by
 *     {@link FloatingOrigin.applyToLocal}, which applies the exact rigid motion (rotation
 *     included) rather than the bare shift.
 *
 * The `shift` fields below are the honest *translation* component, useful for diagnostics and
 * for anything within a few tens of metres of the aeroplane. They are not a substitute for
 * either of the above: measured, a bare translation is already 0.5 m out at 640 m.
 */
export interface OriginRebaseEvent {
  /**
   * How far the origin moved, in the *old* frame's sim ENU metres. Add this to a position to
   * express it in the new frame — exact near the aircraft, approximate far from it. See above.
   */
  shiftEastM: number;
  shiftNorthM: number;
  shiftUpM: number;
  /** Rebases since construction. */
  count: number;
}

/**
 * Keeps the local frame's origin near the aircraft (non-negotiable #4).
 *
 * FP32 has 24 bits of mantissa: at 10 km from the origin a float resolves to about 0.6 mm, at
 * 100 km to 6 mm, and at 1000 km to 60 mm. Vertex positions, matrices and depth all go through
 * FP32 on the GPU, so past a few tens of kilometres the world visibly quantises — the cockpit
 * shakes, shadows crawl, and coplanar surfaces start z-fighting. Rebasing every 10 km keeps
 * everything the GPU sees inside the range where a float is effectively exact.
 *
 * Rebasing moves the origin **horizontally only**, to the aircraft's current latitude and
 * longitude, keeping the altitude datum at the ellipsoid. That means `up` is always altitude
 * MSL — no rebase can ever change what the altimeter reads, which is the sort of thing that
 * would otherwise produce a 3 a.m. bug report about the aeroplane teleporting 8 m every time it
 * crosses an invisible line.
 *
 * ## The frozen-frame approximation, and what it costs
 *
 * Between rebases the east/north directions are held fixed, so an aircraft flying "due east" in
 * the local frame is really flying a sequence of east-heading great-circle segments rather than
 * a rhumb line, and drifts about 6 m towards the equator per 10 km leg at mid latitudes
 * (`d²·tanφ / 2R`). This is the standard tangent-plane trade and it is invisible in flight: the
 * terrain, the airports and the aircraft are all expressed in the same frame, so nothing moves
 * relative to anything else and there is no visual artefact whatsoever. It shows up only as a
 * navigation nicety — a 200 km leg finishes roughly 120 m south of where a compass heading
 * would have put it. Shrinking the rebase distance shrinks it quadratically if a future
 * navigation feature ever cares.
 *
 * `updateFixed(dt)`: called once per physics tick with the aircraft position. Allocation-free.
 */
export class FloatingOrigin {
  readonly frame: LocalFrame;
  /** The frame as it was before the most recent rebase — see {@link applyToLocal}. */
  readonly previousFrame: LocalFrame;
  readonly rebaseDistanceM: number;

  private countValue = 0;
  private readonly geodeticScratch: Geodetic = makeGeodetic();
  private readonly ecefScratch: Ecef = makeEcef();
  private readonly event: OriginRebaseEvent = {
    shiftEastM: 0,
    shiftNorthM: 0,
    shiftUpM: 0,
    count: 0,
  };

  constructor(
    latitudeDeg = 0,
    longitudeDeg = 0,
    rebaseDistanceM: number = ORIGIN_REBASE_DISTANCE_M,
  ) {
    this.frame = new LocalFrame(latitudeDeg, longitudeDeg, 0);
    this.previousFrame = new LocalFrame(latitudeDeg, longitudeDeg, 0);
    this.rebaseDistanceM = rebaseDistanceM;
  }

  /**
   * Carry a sim-frame position from the pre-rebase frame into the current one, exactly.
   *
   * This is the correct way to move anything that has no latitude and longitude of its own —
   * smoke, contrails, tracers, wreckage. It routes the point out through ECEF and back, so the
   * frame's rotation is handled rather than ignored, and it is exact to double precision: the
   * ellipsoidal tangent height is solved for on the way out and the altitude is read straight
   * back off the ECEF point on the way in, so neither end leans on the cheap curvature term.
   *
   * Costs a few geodetic inversions per point. That is far too expensive per vertex and
   * completely free at the rate it actually runs — a few thousand particles once every 10 km.
   *
   * Only meaningful in the update immediately after {@link update} returned an event.
   * Allocation-free; safe to call with `out` aliasing the input.
   */
  applyToLocal(eastM: number, northM: number, upM: number, out: Vector3): Vector3 {
    const previous = this.previousFrame;
    const tangentUp = previous.exactLocalUpToTangentUp(eastM, northM, upM);
    const p = previous.tangentToEcef(eastM, northM, tangentUp, this.ecefScratch);
    this.frame.ecefToTangent(p.x, p.y, p.z, out);
    ecefToGeodetic(p.x, p.y, p.z, this.geodeticScratch);
    out.z = this.geodeticScratch.altitudeM - this.frame.origin.altitudeM;
    return out;
  }

  get rebaseCount(): number {
    return this.countValue;
  }

  /** Move the origin to a specific point, e.g. when spawning at a new airport. */
  reanchor(latitudeDeg: number, longitudeDeg: number): void {
    this.frame.setOrigin(latitudeDeg, longitudeDeg, 0);
  }

  /** Would a position this far out trigger a rebase? */
  needsRebase(eastM: number, northM: number): boolean {
    return eastM * eastM + northM * northM > this.rebaseDistanceM * this.rebaseDistanceM;
  }

  /**
   * Check the aircraft's sim-frame position and rebase if it has wandered too far.
   *
   * @returns the rebase event when the origin moved, otherwise null. The event's shift is what
   *   every other position in the world must be translated by; it is the *reused* struct, so
   *   read it now rather than keeping it.
   */
  update(eastM: number, northM: number): OriginRebaseEvent | null {
    if (!this.needsRebase(eastM, northM)) return null;

    // Where the aircraft actually is, on the planet.
    this.frame.localToGeodetic(eastM, northM, 0, this.geodeticScratch);
    this.previousFrame.copyFrom(this.frame);
    this.frame.setOrigin(this.geodeticScratch.latitudeDeg, this.geodeticScratch.longitudeDeg, 0);

    this.countValue++;
    // Everything else in the world moves the opposite way to the origin.
    this.event.shiftEastM = -eastM;
    this.event.shiftNorthM = -northM;
    this.event.shiftUpM = 0;
    this.event.count = this.countValue;
    return this.event;
  }
}
